import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./CDP.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Database,
  Users,
  GitBranch,
  Cpu,
  Zap,
  Target,
  TrendingUp,
  Layers,
  Server,
  Cloud,
  Lock,
  CheckCircle2,
  Activity,
  PieChart,
  BarChart3,
  Network,
  Workflow,
  Brain,
  Sparkles,
  Mail,
  MessageSquare,
  Monitor,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

// ============================================
// CDP COMPLEX STATIC VISUALIZATIONS
// ============================================

// Data Pipeline Visualization for Hero - Network/Flow Style
const DataSourcesVisualization = () => {
  const dataSources = [
    { id: 'web', label: 'Web', value: '2.4M', color: '#3b82f6' },
    { id: 'crm', label: 'CRM', value: '890K', color: '#8b5cf6' },
    { id: 'mobile', label: 'Mobile', value: '1.2M', color: '#10b981' },
    { id: 'trans', label: 'Transactions', value: '567K', color: '#f59e0b' },
    { id: 'social', label: 'Social', value: '340K', color: '#ec4899' },
    { id: 'email', label: 'Email', value: '1.8M', color: '#06b6d4' },
  ];

  const outputChannels = [
    { label: 'Segments', count: '847' },
    { label: 'Audiences', count: '124' },
    { label: 'Campaigns', count: '56' },
  ];

  return (
    <div className="cdp-viz-container pipeline-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">REAL-TIME DATA PIPELINE</span>
        <span className="cdp-viz-badge green">PROCESSING</span>
      </div>
      
      <div className="pipeline-layout">
        {/* Left side - Data Sources */}
        <div className="pipeline-sources">
          {dataSources.map((source, i) => (
            <div key={i} className="pipeline-source-node" style={{ '--node-color': source.color }}>
              <div className="node-dot" style={{ backgroundColor: source.color }}></div>
              <div className="node-content">
                <span className="node-label">{source.label}</span>
                <span className="node-value">{source.value}</span>
              </div>
              <div className="node-connector">
                <svg viewBox="0 0 40 10" className="connector-svg">
                  <line x1="0" y1="5" x2="35" y2="5" stroke={source.color} strokeWidth="1.5" strokeDasharray="3 2"/>
                  <circle cx="38" cy="5" r="2" fill={source.color}/>
                </svg>
              </div>
            </div>
          ))}
        </div>
        
        {/* Center - Processing Hub */}
        <div className="pipeline-hub">
          <div className="hub-outer">
            <div className="hub-ring"></div>
            <div className="hub-inner">
              <Cpu size={20} />
              <span>CDP</span>
            </div>
          </div>
          <div className="hub-stats">
            <div className="hub-stat">
              <span className="stat-value">7.2M</span>
              <span className="stat-label">records/day</span>
            </div>
          </div>
        </div>
        
        {/* Right side - Outputs */}
        <div className="pipeline-outputs">
          {outputChannels.map((channel, i) => (
            <div key={i} className="pipeline-output-node">
              <div className="output-connector">
                <svg viewBox="0 0 40 10" className="connector-svg">
                  <circle cx="2" cy="5" r="2" fill="#3b82f6"/>
                  <line x1="5" y1="5" x2="40" y2="5" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="3 2"/>
                </svg>
              </div>
              <div className="output-content">
                <span className="output-count">{channel.count}</span>
                <span className="output-label">{channel.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom metrics */}
      <div className="pipeline-metrics">
        <div className="pipe-metric">
          <div className="metric-icon">
            <Zap size={14} />
          </div>
          <div className="metric-text">
            <span className="metric-value">&lt;50ms</span>
            <span className="metric-label">Latency</span>
          </div>
        </div>
        <div className="pipe-metric">
          <div className="metric-icon">
            <Activity size={14} />
          </div>
          <div className="metric-text">
            <span className="metric-value">99.99%</span>
            <span className="metric-label">Uptime</span>
          </div>
        </div>
        <div className="pipe-metric">
          <div className="metric-icon">
            <Lock size={14} />
          </div>
          <div className="metric-text">
            <span className="metric-value">SOC 2</span>
            <span className="metric-label">Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Unified Profile Visualization
const UnifiedProfileVisualization = () => {
  const attributes = [
    { category: 'Identity', items: ['Email', 'Phone', 'Device IDs'], fill: 98 },
    { category: 'Behavior', items: ['Page Views', 'Clicks', 'Sessions'], fill: 87 },
    { category: 'Purchase', items: ['Orders', 'Revenue', 'Frequency'], fill: 92 },
    { category: 'Preference', items: ['Channels', 'Timing', 'Content'], fill: 76 },
  ];

  return (
    <div className="cdp-viz-container unified-profile-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">UNIFIED CUSTOMER PROFILE</span>
        <span className="cdp-viz-id">#CDP-2847291</span>
      </div>
      
      <div className="profile-diagram">
        <div className="profile-center-node">
          <Users size={28} />
          <span>360° View</span>
        </div>
        
        <div className="profile-spokes">
          {attributes.map((attr, i) => (
            <div key={i} className="profile-spoke" style={{ '--angle': `${i * 90}deg` }}>
              <div className="spoke-content">
                <div className="spoke-header">
                  <span className="spoke-category">{attr.category}</span>
                  <span className="spoke-fill">{attr.fill}%</span>
                </div>
                <div className="spoke-items">
                  {attr.items.map((item, j) => (
                    <span key={j} className="spoke-item">{item}</span>
                  ))}
                </div>
                <div className="spoke-bar">
                  <div className="spoke-bar-fill" style={{ width: `${attr.fill}%` }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="profile-metrics-row">
        <div className="prof-metric">
          <Database size={14} />
          <span>12 Sources</span>
        </div>
        <div className="prof-metric">
          <Layers size={14} />
          <span>847 Attributes</span>
        </div>
        <div className="prof-metric">
          <Lock size={14} />
          <span>Compliant</span>
        </div>
      </div>
    </div>
  );
};

// Identity Graph Visualization
const IdentityGraphVisualization = () => {
  return (
    <div className="cdp-viz-container identity-graph-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">IDENTITY GRAPH</span>
        <span className="cdp-viz-badge">245M+ PROFILES</span>
      </div>
      
      <svg viewBox="0 0 200 120" className="identity-svg">
        {/* Grid background */}
        <defs>
          <pattern id="cdpGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f1f5f9" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="200" height="120" fill="url(#cdpGrid)" />
        
        {/* Identity clusters */}
        {/* Cluster 1 */}
        <circle cx="50" cy="40" r="20" fill="#eff6ff" stroke="#3b82f6" strokeWidth="1"/>
        <circle cx="40" cy="35" r="4" fill="#3b82f6"/>
        <circle cx="55" cy="32" r="3" fill="#60a5fa"/>
        <circle cx="60" cy="45" r="5" fill="#2563eb"/>
        <circle cx="45" cy="50" r="3" fill="#93c5fd"/>
        <text x="50" y="65" textAnchor="middle" fontSize="5" fill="#64748b">Known Users</text>
        
        {/* Cluster 2 */}
        <circle cx="150" cy="40" r="18" fill="#f0fdf4" stroke="#10b981" strokeWidth="1"/>
        <circle cx="145" cy="35" r="3" fill="#10b981"/>
        <circle cx="155" cy="38" r="4" fill="#34d399"/>
        <circle cx="148" cy="48" r="3" fill="#6ee7b7"/>
        <text x="150" y="65" textAnchor="middle" fontSize="5" fill="#64748b">Matched</text>
        
        {/* Cluster 3 */}
        <circle cx="100" cy="85" r="15" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1"/>
        <circle cx="95" cy="82" r="3" fill="#f59e0b"/>
        <circle cx="105" cy="85" r="4" fill="#fbbf24"/>
        <circle cx="100" cy="92" r="3" fill="#fcd34d"/>
        <text x="100" y="108" textAnchor="middle" fontSize="5" fill="#64748b">Anonymous</text>
        
        {/* Connection lines */}
        <line x1="70" y1="40" x2="132" y2="40" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 2"/>
        <line x1="60" y1="55" x2="90" y2="75" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 2"/>
        <line x1="140" y1="55" x2="110" y2="75" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 2"/>
        
        {/* Central resolution node */}
        <rect x="90" y="35" width="20" height="12" rx="2" fill="#1e293b"/>
        <text x="100" y="43" textAnchor="middle" fontSize="4" fill="white">RESOLVE</text>
      </svg>
      
      <div className="identity-stats">
        <div className="id-stat">
          <span className="id-value">99.2%</span>
          <span className="id-label">Match Rate</span>
        </div>
        <div className="id-stat">
          <span className="id-value">2.3B</span>
          <span className="id-label">Signals/Day</span>
        </div>
        <div className="id-stat">
          <span className="id-value">Cross-Device</span>
          <span className="id-label">Resolution</span>
        </div>
      </div>
    </div>
  );
};

// Intelligence Dashboard Visualization
const IntelligenceVisualization = () => {
  const insights = [
    { label: 'Propensity to Buy', score: 87, trend: '+12%' },
    { label: 'Churn Risk', score: 23, trend: '-8%' },
    { label: 'Lifetime Value', score: 94, trend: '+5%' },
  ];

  return (
    <div className="cdp-viz-container intelligence-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">PREDICTIVE INTELLIGENCE</span>
        <span className="cdp-viz-badge">AI-POWERED</span>
      </div>
      
      <div className="intel-grid">
        {insights.map((insight, i) => (
          <div key={i} className="intel-card">
            <div className="intel-score-ring">
              <svg viewBox="0 0 36 36" className="score-ring-svg">
                <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="3"/>
                <circle cx="18" cy="18" r="15" fill="none" stroke="#3b82f6" strokeWidth="3"
                  strokeDasharray={`${insight.score} ${100 - insight.score}`}
                  strokeLinecap="round"
                  transform="rotate(-90 18 18)"/>
              </svg>
              <span className="score-value">{insight.score}</span>
            </div>
            <div className="intel-info">
              <span className="intel-label">{insight.label}</span>
              <span className={`intel-trend ${insight.trend.startsWith('+') ? 'positive' : 'negative'}`}>
                {insight.trend}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="intel-features">
        <div className="intel-feature">
          <Brain size={14} />
          <span>ML Models</span>
        </div>
        <div className="intel-feature">
          <Sparkles size={14} />
          <span>Real-time Scoring</span>
        </div>
        <div className="intel-feature">
          <Target size={14} />
          <span>Auto-Segments</span>
        </div>
      </div>
    </div>
  );
};

// AI Agents Visualization
const AIAgentsVisualization = () => {
  const agents = [
    { name: 'Onboarding Agent', status: 'active', tasks: 24 },
    { name: 'Segmentation Agent', status: 'active', tasks: 156 },
    { name: 'Content Agent', status: 'idle', tasks: 0 },
    { name: 'Optimization Agent', status: 'active', tasks: 87 },
  ];

  return (
    <div className="cdp-viz-container ai-agents-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">AI AGENTS</span>
        <span className="cdp-viz-badge green">3 ACTIVE</span>
      </div>
      
      <div className="agents-list">
        {agents.map((agent, i) => (
          <div key={i} className={`agent-row ${agent.status}`}>
            <div className="agent-icon">
              <Cpu size={16} />
            </div>
            <div className="agent-info">
              <span className="agent-name">{agent.name}</span>
              <span className="agent-tasks">{agent.tasks} tasks</span>
            </div>
            <div className={`agent-status-badge ${agent.status}`}>
              {agent.status}
            </div>
          </div>
        ))}
      </div>
      
      <div className="agents-summary">
        <div className="agent-metric">
          <span className="metric-num">267</span>
          <span className="metric-txt">Tasks Today</span>
        </div>
        <div className="agent-metric">
          <span className="metric-num">4.2h</span>
          <span className="metric-txt">Time Saved</span>
        </div>
      </div>
    </div>
  );
};

// Activation Pipeline Visualization
const ActivationVisualization = () => {
  const channels = [
    { icon: Mail, name: 'Email', sent: '12.4K' },
    { icon: MessageSquare, name: 'SMS', sent: '8.2K' },
    { icon: Monitor, name: 'Ads', sent: '45.1K' },
    { icon: Globe, name: 'Web', sent: '23.7K' },
  ];

  return (
    <div className="cdp-viz-container activation-viz">
      <div className="cdp-viz-header">
        <span className="cdp-viz-label">REAL-TIME ACTIVATION</span>
        <span className="cdp-viz-badge">LIVE</span>
      </div>
      
      <div className="activation-pipeline">
        <div className="pipeline-stage source">
          <div className="stage-box">
            <Database size={18} />
            <span>CDP</span>
          </div>
        </div>
        
        <div className="pipeline-arrow">
          <svg viewBox="0 0 40 20">
            <path d="M0,10 L30,10 M25,5 L30,10 L25,15" fill="none" stroke="#3b82f6" strokeWidth="2"/>
          </svg>
        </div>
        
        <div className="pipeline-stage processor">
          <div className="stage-box">
            <Zap size={18} />
            <span>Engine</span>
          </div>
        </div>
        
        <div className="pipeline-arrow">
          <svg viewBox="0 0 40 20">
            <path d="M0,10 L30,10 M25,5 L30,10 L25,15" fill="none" stroke="#3b82f6" strokeWidth="2"/>
          </svg>
        </div>
        
        <div className="pipeline-stage outputs">
          <div className="channels-stack">
            {channels.map((ch, i) => (
              <div key={i} className="channel-mini">
                <ch.icon size={14} />
                <span>{ch.name}</span>
                <span className="ch-sent">{ch.sent}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="activation-metrics">
        <div className="act-metric">
          <span className="act-value">89.4K</span>
          <span className="act-label">Messages/Hour</span>
        </div>
        <div className="act-metric">
          <span className="act-value">&lt;50ms</span>
          <span className="act-label">Avg Latency</span>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const CDPHero = () => {
  return (
    <section className="cdp-hero" data-testid="cdp-hero">
      <div className="cdp-hero-content">
        <div className="cdp-hero-text">
          <span className="cdp-label">DATA INTELLIGENCE</span>
          <h1>Proprietary Consumer Data at Scale</h1>
          <p>
            Access 245M+ enriched consumer profiles with behavioral, transactional, 
            and intent signals. Our data infrastructure enables precise audience 
            identification and predictive targeting without complex integrations.
          </p>
          <Link to="/contact">
            <Button className="btn-primary-zeta">
              Explore Capabilities
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
        <div className="cdp-hero-visual">
          <DataSourcesVisualization />
        </div>
      </div>
    </section>
  );
};

// Intro Section
const CDPIntro = () => {
  return (
    <section className="cdp-intro" data-testid="cdp-intro">
      <div className="cdp-intro-content">
        <span className="cdp-intro-label">Our Data. Your Advantage.</span>
        <p>
          We've built one of the largest consumer intelligence platforms in marketing. 
          Our continuously enriched database powers precision targeting, predictive 
          modeling, and measurable campaign outcomes.
        </p>
      </div>
    </section>
  );
};

// Feature Section Component
const CDPFeatureSection = ({ 
  title, 
  description, 
  bullets, 
  visualization: Visualization,
  reversed = false,
  linkText,
  linkHref
}) => {
  return (
    <section className={`cdp-feature-section ${reversed ? 'reversed' : ''}`}>
      <div className="cdp-feature-wrapper">
        <div className="cdp-feature-visual">
          <Visualization />
        </div>
        <div className="cdp-feature-text">
          <h2>{title}</h2>
          <p className="cdp-feature-desc">{description}</p>
          <div className="cdp-bullet-list">
            {bullets.map((bullet, index) => (
              <div key={index} className="cdp-bullet-item">
                <div className="cdp-bullet-bar"></div>
                <p>{bullet}</p>
              </div>
            ))}
          </div>
          {linkText && (
            <a href={linkHref} className="cdp-feature-link">
              {linkText} <ArrowRight size={16} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

// Testimonial/Quote Section
const CDPQuote = () => (
  <section className="cdp-quote-section">
    <div className="cdp-quote-content">
      <blockquote>
        "IntentLogic's data capabilities and campaign execution delivered the highest 
        performance in Data Enrichment, Audience Targeting, and Journey Orchestration 
        we've ever seen."
      </blockquote>
      <cite>— Enterprise Client, 2024</cite>
    </div>
  </section>
);

// FAQ Section
const CDPFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How does IntentLogic use data to improve my campaigns?",
      answer: "We unify your data, resolve identities, enrich profiles, and activate campaigns in real-time. Our team handles all the complexity so you get data-driven campaigns that respond to behaviors and signals in the moment."
    },
    {
      question: "Why is identity resolution important for my marketing?",
      answer: "Identity resolution ensures your customer data is accurate and actionable. We use our Identity Graph to connect billions of signals to individuals, enabling precise targeting and personalization across devices."
    },
    {
      question: "How does IntentLogic enrich my customer data?",
      answer: "We transform static profiles into dynamic assets by appending predictive signals to every profile. This enables us to act on intent, behavior, and lifecycle triggers as they happen in your campaigns."
    },
    {
      question: "How do you deliver personalized experiences for my brand?",
      answer: "We integrate data with messaging and activation to deliver seamless, personalized experiences across all channels. We use real-time insights to tailor customer journeys and drive deeper engagement."
    },
    {
      question: "What results can I expect from working with IntentLogic?",
      answer: "Our clients typically see higher engagement rates, improved conversion, and reduced media waste. We make every customer interaction more valuable through unified profiles and predictive insights."
    }
  ];

  return (
    <section className="cdp-faq-section" data-testid="cdp-faq">
      <h2>FAQs</h2>
      <div className="cdp-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`cdp-faq-item ${openIndex === index ? 'open' : ''}`}
            data-testid={`faq-item-${index}`}
          >
            <button 
              className="cdp-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="cdp-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const CDPCTA = () => (
  <section className="cdp-cta-section" id="cdp-cta" data-testid="cdp-cta">
    <div className="cdp-cta-content">
      <h2>Let Us Power Your Campaigns with Data</h2>
      <p>
        Whether you need to unify fragmented data, accelerate engagement, or drive 
        measurable outcomes—our team handles it all so you can focus on growth.
      </p>
      <Link to="/contact">
        <Button className="btn-primary-zeta">
          Schedule Consultation
          <ArrowRight size={18} />
        </Button>
      </Link>
    </div>
  </section>
);

// Main CDP Page Component
function CDPPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO 
        title="Data Platform - Consumer Intelligence & Identity Resolution"
        description="Access 245M+ enriched consumer profiles with behavioral, transactional, and intent signals. Our proprietary data infrastructure enables precise audience identification and predictive targeting."
        keywords="customer data platform, CDP, consumer data, identity resolution, first-party data, audience targeting, data management, customer profiles, behavioral data, intent signals"
        canonical="/cdp"
      />
      <CDPHero />
      <CDPIntro />
      
      <CDPFeatureSection
        title="245M+ Unified Consumer Profiles"
        description="Our database contains comprehensive profiles enriched with demographic, behavioral, and intent signals—ready for immediate activation."
        bullets={[
          "Access enriched profiles across the U.S. consumer population.",
          "Behavioral, transactional, and intent signals continuously updated.",
          "No integration complexity—data is activation-ready."
        ]}
        visualization={UnifiedProfileVisualization}
        linkText="View Data Specifications"
        linkHref="/#channels"
      />
      
      <CDPFeatureSection
        title="Cross-Device Identity Resolution"
        description="Our proprietary identity graph connects consumers across devices, channels, and touchpoints for consistent targeting and measurement."
        bullets={[
          "Deterministic and probabilistic matching across our database.",
          "Real-time profile enrichment with new behavioral signals.",
          "Consistent identity across email, mobile, web, and advertising."
        ]}
        visualization={IdentityGraphVisualization}
        reversed
      />
      
      <CDPQuote />
      
      <CDPFeatureSection
        title="Predictive Intelligence Layer"
        description="Advanced analytics applied to our data generates predictive scores and propensity models for precise audience segmentation."
        bullets={[
          "Intent signals, purchase propensity, and behavioral predictions.",
          "Custom model development for specific business objectives.",
          "Continuous learning from campaign performance data."
        ]}
        visualization={IntelligenceVisualization}
      />
      
      <CDPFeatureSection
        title="AI-Powered Optimization"
        description="Machine learning algorithms optimize targeting, creative selection, and budget allocation based on real-time performance signals."
        bullets={[
          "Dynamic audience refinement based on conversion patterns.",
          "Automated creative and channel optimization.",
          "Predictive budget allocation across campaigns."
        ]}
        visualization={AIAgentsVisualization}
        reversed
      />
      
      <CDPFeatureSection
        title="Seamless Campaign Activation"
        description="Direct integration with major advertising platforms and messaging channels enables rapid campaign deployment and optimization."
        bullets={[
          "Native connections to DSPs, social platforms, and messaging systems.",
          "Unified workflow from audience creation to campaign execution.",
          "Real-time performance feedback for continuous optimization."
        ]}
        visualization={ActivationVisualization}
      />
      
      <CDPFAQ />
      <CDPCTA />
    </Layout>
  );
}

export default CDPPage;
